import { BarChart } from "@/components/charts/bar-chart";
import { DecisionsBox } from "@/components/my/decisions-box";
import { NewItemsBox } from "@/components/my/new-items-box";
import { ProjectStrip } from "@/components/my/project-strip";
import { AppLink } from "@/components/ui/app-link";
import { Card, CardContent } from "@/components/ui/card";
import {
  type WorkItemRow as TableRow,
  WorkItemsTable,
} from "@/components/work-items/work-items-table";
import { api } from "@/lib/api";
import { formatFixed, formatNumber } from "@/lib/i18n";
import type { MyDecisions } from "@/lib/my-decisions";
import { projectHref } from "@/lib/project-href";
import { formatHours } from "@/lib/utils";
import { createFileRoute, redirect, useLoaderData } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/my` — the signed-in user's landing dashboard. Port of the
 * Next `(app)/my/page.tsx`. The 11-query aggregate moved to
 * `GET /api/me/dashboard`; this page just renders the pre-shaped
 * response.
 *
 * Work-item links use `workItemHref` (the GitLab-style URL,
 * still served by Next). Project links inside the strip do the
 * same. Both fall through to a full navigation until those
 * detail pages port.
 */

type ProjectRef = {
  id: string;
  slug: string;
  name: string;
  group: { path: string } | null;
};

type WorkItemRow = {
  id: string;
  title: string;
  gitlabIssueId: string;
  globalId: number;
  status: { name: string; color: string } | null;
  project: ProjectRef;
};

type DashboardData = {
  userName: string | null;
  projects: Array<{
    id: string;
    name: string;
    slug: string;
    group: { path: string } | null;
    assignedCount: number;
    roles: string[];
  }>;
  newItems: {
    total: number;
    byProject: Array<{ id: string; name: string; slug: string; count: number }>;
  };
  assignedItems: Array<WorkItemRow & { priority: string }>;
  watchedItems: Array<{
    id: string;
    title: string;
    gitlabIssueId: string;
    globalId: number;
    project: ProjectRef;
    lastJournalAt: string | null;
  }>;
  authoredItems: WorkItemRow[];
  week: { total: number; entryCount: number; byDay: Array<{ label: string; value: number }> };
  month: {
    total: number;
    entryCount: number;
    byProject: Array<{ label: string; value: number }>;
  };
  pendingApprovals: {
    count: number;
    items: Array<{ id: string; label: string; hours: number }>;
  };
  approvedUnpaid: { count: number; hours: number };
  /** YTD hours + amount (at the cost rate active on the entry's
   *  `spentOn`) bucketed by approval state, plus the
   *  rejected/total ratio. Untriaged rows are excluded since they
   *  have no project to resolve a rate against. */
  statusBreakdown: {
    worked: { hours: number; amount: number };
    budget: { hours: number; amount: number };
    approved: { hours: number; amount: number };
    paid: { hours: number; amount: number };
    rejected: { hours: number; amount: number };
    totalHours: number;
    rejectedPct: number;
  };
  recentJournals: Array<{
    id: string;
    field: string;
    oldValue: string | null;
    newValue: string | null;
    userName: string | null;
    createdAt: string;
    workItem: { title: string; gitlabIssueId: string; globalId: number; project: ProjectRef };
  }>;
  priorityCounts: Record<string, number>;
};

/** The slice of `/api/work-items/list` the "Assigned to me" table
 *  needs — same payload the `/work-items` list page renders. */
type AssignedWorkItems = {
  workItems: TableRow[];
  total: number;
  page: number;
  pageSize: number;
  statuses: Array<{ id: string; name: string; color: string }>;
  users: Array<{ id: string; name: string }>;
};

/**
 * Cap on the per-call work-items list. The "Assigned to me"
 * section renders one card per project the viewer has tasks in;
 * a single big page covers every project in one shot rather than
 * paginating across them (200 assigned tasks is already an
 * outlier).
 */
const ASSIGNED_PAGE_SIZE = 200;

const mySearchSchema = z.object({});

export const Route = createFileRoute("/_app/my/")({
  validateSearch: mySearchSchema,
  loader: async ({ parentMatchPromise }) => {
    if ((await parentMatchPromise).loaderData?.isCustomer) throw redirect({ to: "/dashboard" });
    const params = new URLSearchParams({
      assignee: "me",
      excludeStatus: "new",
      pageSize: String(ASSIGNED_PAGE_SIZE),
    });
    const [dashboard, assigned, decisions] = await Promise.all([
      api.get<DashboardData>("/api/me/dashboard"),
      // The very same listing the `/work-items` page produces for
      // "assignee = me", so the two surfaces match exactly.
      api.get<AssignedWorkItems>(`/api/work-items/list?${params.toString()}`),
      // The box says it couldn't load; the rest of the page still renders.
      api
        .get<MyDecisions>("/api/me/decisions")
        .catch(() => null),
    ]);
    return { dashboard, assigned, decisions };
  },
  component: MyPage,
});

/** `projectHref` throws for projects with no GitLab binding;
 *  surface that as `null` so the per-project header drops the link. */
function projectHrefOrNull(project: TableRow["project"]): string | null {
  try {
    return projectHref(project);
  } catch {
    return null;
  }
}

/** Bucket work-item rows by their project, preserving the input
 *  order both for the projects (first-seen-wins) and the rows
 *  inside each bucket. The server already returns rows in the
 *  "Assigned to me" ordering, so the cards on the page inherit it. */
function groupByProject(
  rows: TableRow[],
): Array<{ project: TableRow["project"]; rows: TableRow[] }> {
  const buckets = new Map<string, { project: TableRow["project"]; rows: TableRow[] }>();
  for (const r of rows) {
    const existing = buckets.get(r.project.id);
    if (existing) {
      existing.rows.push(r);
    } else {
      buckets.set(r.project.id, { project: r.project, rows: [r] });
    }
  }
  return Array.from(buckets.values());
}

/** Per-project assigned-to-me card on the My Page. The viewer's
 *  workload is dense enough that any one project may carry dozens
 *  of items — page through five at a time inside the box rather
 *  than make the card scroll, and link "View all" out to the
 *  project's full work-item list pre-filtered to the viewer. */
const ROWS_PER_CARD = 5;

function AssignedProjectCard({
  project,
  rows,
  users,
  currentUserId,
}: {
  project: TableRow["project"];
  rows: TableRow[];
  users: AssignedWorkItems["users"];
  currentUserId: string;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const projectHrefValue = projectHrefOrNull(project);
  const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_CARD));
  const safePage = Math.min(page, totalPages - 1);
  const visibleRows = rows.slice(safePage * ROWS_PER_CARD, (safePage + 1) * ROWS_PER_CARD);
  // Pass the viewer's actual user id, not the `me` token: the
  // work-items page's assignee combobox keys off the user id and
  // can't pre-select a value it doesn't have an option for. The
  // server still accepts `assignee=me` for legacy callers.
  const viewAllHref = `/projects/${project.slug}/work-items?assignee=${currentUserId}`;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          {projectHrefValue ? (
            <AppLink
              href={projectHrefValue}
              className="font-heading text-base font-semibold hover:underline"
            >
              {project.name}
            </AppLink>
          ) : (
            <span className="font-heading text-base font-semibold">{project.name}</span>
          )}
          <div className="flex items-center gap-3 text-xs">
            <span className="text-muted-foreground">{t("my.tasks", { count: rows.length })}</span>
            <AppLink href={viewAllHref} className="text-primary hover:underline">
              {t("my.viewAll")}
            </AppLink>
          </div>
        </div>
        <WorkItemsTable
          workItems={visibleRows}
          users={users}
          currentUserId={currentUserId}
          compact
        />
        {rows.length > ROWS_PER_CARD && (
          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              className="text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
            >
              {t("my.previous")}
            </button>
            <span className="text-muted-foreground">
              {t("my.pageOf", { page: safePage + 1, total: totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage === totalPages - 1}
              className="text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
            >
              {t("my.next")}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MyPage() {
  const { t } = useTranslation();
  const { dashboard: data, assigned, decisions } = Route.useLoaderData();
  const layout = useLoaderData({ from: "/_app" });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold">{t("my.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("my.welcome", { name: data.userName ?? t("my.defaultUserName") })}
        </p>
      </div>

      <DecisionsBox data={decisions} />

      {data.projects.length > 0 && (
        <ProjectStrip projects={data.projects} viewerId={layout.user.id} />
      )}

      {/* This week | This month */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t("my.thisWeek")}
              </h2>
              <AppLink
                href="/time-entries?period=week"
                className="text-xs text-primary hover:underline"
              >
                {t("my.details")}
              </AppLink>
            </div>
            <p className="font-heading text-3xl font-bold text-primary">
              {formatHours(data.week.total)}
            </p>
            <p className="text-xs text-muted-foreground mb-2">
              {t("my.entries", { count: data.week.entryCount })}
            </p>
            <BarChart
              data={data.week.byDay}
              showValues={false}
              orientation="vertical"
              formatValue={(v) => `${formatFixed(v, 1)}h`}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t("my.thisMonth")}
              </h2>
              <AppLink
                href="/time-entries?period=month"
                className="text-xs text-primary hover:underline"
              >
                {t("my.details")}
              </AppLink>
            </div>
            <p className="font-heading text-3xl font-bold text-primary">
              {formatHours(data.month.total)}
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              {t("my.entries", { count: data.month.entryCount })}
            </p>
            <BarChart
              data={data.month.byProject}
              orientation="horizontal"
              showValues
              formatValue={(v) => `${formatFixed(v, 1)}h`}
            />
            {data.month.byProject.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("my.noEntriesThisMonth")}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* YTD per-state strip — inline (no Card), three hours+amount
       *  buckets (worked / budget / approved) and a rejected-share
       *  percentage. Paid is omitted on purpose — the YTD figure
       *  doesn't add anything beyond what `approved` already shows.
       *
       *  Defensive default: an older server payload that doesn't
       *  carry `statusBreakdown` renders zeros instead of crashing
       *  the page. */}
      {(() => {
        const empty = { hours: 0, amount: 0 };
        const sb = data.statusBreakdown ?? {
          worked: empty,
          budget: empty,
          approved: empty,
          rejected: empty,
          totalHours: 0,
          rejectedPct: 0,
        };
        return (
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <StatusStat label={t("my.status.worked")} tone="secondary" bucket={sb.worked} />
            <StatusStat label={t("my.status.budget")} tone="warning" bucket={sb.budget} />
            <StatusStat label={t("my.status.approved")} tone="success" bucket={sb.approved} />
            <div className="rounded-md border border-l-4 border-l-red-500 bg-background px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {t("my.status.rejected")}
              </p>
              <p className="font-heading text-lg font-semibold">
                {formatFixed(sb.rejectedPct, 1)}%
              </p>
              <p className="text-[11px] text-muted-foreground">
                {t("my.status.rejectedOf", { hours: formatHours(sb.totalHours) })}
              </p>
            </div>
          </div>
        );
      })()}

      <NewItemsBox
        items={data.newItems}
        viewerId={layout.user.id}
        newStatusId={assigned.statuses.find((s) => s.name === "new")?.id}
      />

      {/* Assigned to me — one card per project, two columns on
       *  large screens. The single-table version pre-2026-05-20
       *  buried which project owned which task; per-project boxes
       *  make the workload distribution obvious at a glance. */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("my.assignedToMe")}
          </h2>
          <AppLink
            href={`/work-items?assignee=${layout.user.id}`}
            className="text-xs text-primary hover:underline"
          >
            {t("my.viewAll")}
          </AppLink>
        </div>
        {assigned.workItems.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-sm text-muted-foreground text-center">
              {t("my.nothingAssigned")}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
            {groupByProject(assigned.workItems).map((group) => (
              <AssignedProjectCard
                key={group.project.id}
                project={group.project}
                rows={group.rows}
                users={assigned.users}
                currentUserId={layout.user.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Cell on the inline YTD status strip — left-border tone matches
 *  the approval-state Badge variants in the listing so the same
 *  color = same meaning across surfaces. No card wrapper: the
 *  strip is meant to sit lightly above the rest of the page. */
function StatusStat({
  label,
  tone,
  bucket,
}: {
  label: string;
  tone: "secondary" | "warning" | "success";
  bucket: { hours: number; amount: number };
}) {
  const toneClasses: Record<typeof tone, string> = {
    secondary: "border-l-blue-400",
    warning: "border-l-amber-400",
    success: "border-l-emerald-500",
  };
  return (
    <div className={`rounded-md border border-l-4 ${toneClasses[tone]} bg-background px-3 py-2`}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-heading text-lg font-semibold">{formatHours(bucket.hours)}</p>
      <p className="text-xs text-muted-foreground">{formatCurrencyCZK(bucket.amount)}</p>
    </div>
  );
}

function formatCurrencyCZK(value: number): string {
  return formatNumber(value, undefined, {
    style: "currency",
    currency: "CZK",
    maximumFractionDigits: 0,
  });
}
